import React from 'react';

interface OAuthExtras {
  redirectUri?: string;
  codeVerifier?: string;
}

interface OAuthButtonProps {
  provider: 'gmail' | 'linkedin' | 'facebook' | 'twitter';
  onOAuthLogin: (provider: string, token: string, extras?: OAuthExtras) => Promise<void>;
  disabled?: boolean;
  mode?: 'login' | 'register';
}

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#0077B5"/>
  </svg>
);

const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2"/>
  </svg>
);

const TwitterIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="#000000"/>
  </svg>
);

const providerConfig = {
  gmail: {
    name: 'Google',
    Icon: GoogleIcon,
    bg: '#fff',
    border: '#dadce0',
    color: '#3c4043',
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
  },
  linkedin: {
    name: 'LinkedIn',
    Icon: LinkedInIcon,
    bg: '#fff',
    border: '#dadce0',
    color: '#3c4043',
    clientId: process.env.REACT_APP_LINKEDIN_CLIENT_ID,
  },
  facebook: {
    name: 'Facebook',
    Icon: FacebookIcon,
    bg: '#fff',
    border: '#dadce0',
    color: '#3c4043',
    clientId: process.env.REACT_APP_FACEBOOK_CLIENT_ID,
  },
  twitter: {
    name: 'X (Twitter)',
    Icon: TwitterIcon,
    bg: '#fff',
    border: '#dadce0',
    color: '#3c4043',
    clientId: process.env.REACT_APP_TWITTER_CLIENT_ID,
  },
};

const OAuthButton: React.FC<OAuthButtonProps> = ({
  provider,
  onOAuthLogin,
  disabled = false,
  mode = 'login',
}) => {
  const config = providerConfig[provider];
  const { Icon } = config;

  const handleOAuthClick = async () => {
    if (!config.clientId) {
      console.error(`${config.name} OAuth not configured`);
      return;
    }
    try {
      let token: string;
      let extras: OAuthExtras | undefined;
      switch (provider) {
        case 'gmail':    token = await initiateGoogleOAuth(); break;
        case 'linkedin': { const r = await initiateLinkedInOAuth(); token = r.token; extras = r.extras; break; }
        case 'facebook': token = await initiateFacebookOAuth(); break;
        case 'twitter':  { const r = await initiateTwitterOAuth(); token = r.token; extras = r.extras; break; }
        default: throw new Error(`Unsupported provider: ${provider}`);
      }
      await onOAuthLogin(provider, token, extras);
    } catch (error) {
      console.error(`${config.name} OAuth error:`, error);
    }
  };

  const initiateGoogleOAuth = (): Promise<string> =>
    new Promise((resolve, reject) => {
      const init = () => {
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: config.clientId,
          scope: 'email profile',
          callback: (response: any) => {
            if (response.error) reject(new Error(response.error));
            else if (response.access_token) resolve(response.access_token);
            else reject(new Error('Google OAuth failed: no access token'));
          },
        });
        tokenClient.requestAccessToken();
      };
      if (!window.google) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.onload = init;
        script.onerror = () => reject(new Error('Failed to load Google OAuth'));
        document.head.appendChild(script);
      } else {
        init();
      }
    });

  const initiateLinkedInOAuth = (): Promise<{ token: string; extras: OAuthExtras }> =>
    new Promise((resolve, reject) => {
      const state = Math.random().toString(36).substring(7);
      const redirectUri = `${window.location.origin}/oauth/linkedin/callback`;
      const authUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${config.clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}&scope=${encodeURIComponent('openid profile email')}`;
      const popup = window.open(authUrl, 'linkedin-oauth', 'width=600,height=600');
      const check = setInterval(() => { if (popup?.closed) { clearInterval(check); reject(new Error('OAuth popup closed')); } }, 1000);
      const handler = (e: MessageEvent) => {
        if (e.origin !== window.location.origin) return;
        if (e.data.type !== 'LINKEDIN_OAUTH_CODE' && e.data.type !== 'LINKEDIN_OAUTH_ERROR') return;
        clearInterval(check); popup?.close(); window.removeEventListener('message', handler);
        if (e.data.type === 'LINKEDIN_OAUTH_ERROR') { reject(new Error(e.data.error)); return; }
        resolve({ token: e.data.code, extras: { redirectUri: e.data.redirectUri } });
      };
      window.addEventListener('message', handler);
    });

  const initiateFacebookOAuth = (): Promise<string> =>
    new Promise((resolve, reject) => {
      const init = () => {
        window.FB.init({ appId: config.clientId, cookie: true, xfbml: true, version: 'v18.0' });
        window.FB.login((r: any) => {
          if (r.authResponse) resolve(r.authResponse.accessToken);
          else reject(new Error('Facebook OAuth failed'));
        }, { scope: 'email' });
      };
      if (!window.FB) {
        const script = document.createElement('script');
        script.src = 'https://connect.facebook.net/en_US/sdk.js';
        script.onload = init;
        script.onerror = () => reject(new Error('Failed to load Facebook SDK'));
        document.head.appendChild(script);
      } else {
        window.FB.login((r: any) => {
          if (r.authResponse) resolve(r.authResponse.accessToken);
          else reject(new Error('Facebook OAuth failed'));
        }, { scope: 'email' });
      }
    });

  const initiateTwitterOAuth = async (): Promise<{ token: string; extras: OAuthExtras }> => {
    const array = new Uint32Array(28);
    crypto.getRandomValues(array);
    const codeVerifier = Array.from(array, n => ('0' + n.toString(16)).slice(-2)).join('');
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
    const codeChallenge = btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(digest)))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    const state = Math.random().toString(36).substring(7);
    const redirectUri = `${window.location.origin}/oauth/twitter/callback`;
    const authUrl = `https://twitter.com/i/oauth2/authorize?response_type=code&client_id=${config.clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent('tweet.read users.read')}&state=${state}&code_challenge=${codeChallenge}&code_challenge_method=S256`;
    return new Promise((resolve, reject) => {
      const popup = window.open(authUrl, 'twitter-oauth', 'width=600,height=600');
      const check = setInterval(() => { if (popup?.closed) { clearInterval(check); reject(new Error('OAuth popup closed')); } }, 1000);
      const handler = (e: MessageEvent) => {
        if (e.origin !== window.location.origin) return;
        if (e.data.type !== 'TWITTER_OAUTH_CODE' && e.data.type !== 'TWITTER_OAUTH_ERROR') return;
        clearInterval(check); popup?.close(); window.removeEventListener('message', handler);
        if (e.data.type === 'TWITTER_OAUTH_ERROR') { reject(new Error(e.data.error)); return; }
        if (e.data.state !== state) { reject(new Error('Invalid OAuth state')); return; }
        resolve({ token: e.data.code, extras: { redirectUri, codeVerifier } });
      };
      window.addEventListener('message', handler);
    });
  };

  return (
    <button
      type="button"
      onClick={handleOAuthClick}
      disabled={disabled}
      title={`${mode === 'login' ? 'Sign in' : 'Sign up'} with ${config.name}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        width: '100%',
        padding: '9px 12px',
        background: config.bg,
        border: `1.5px solid ${config.border}`,
        borderRadius: 'var(--radius-sm)',
        color: config.color,
        fontSize: 14,
        fontWeight: 500,
        fontFamily: 'var(--font-sans)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'opacity 150ms ease, box-shadow 150ms ease',
        boxShadow: '0 1px 3px rgba(0,0,0,.08)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      }}
    >
      <span style={{ flexShrink: 0, display: 'flex' }}><Icon /></span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{config.name}</span>
    </button>
  );
};

declare global {
  interface Window { google: any; FB: any; }
}

export default OAuthButton;
